import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Tags from '~/components/Tags'

const TagsPage: Component = () => {
  const sampleTags = ['Arquitectura', 'Microservicios', 'Cloud Native', 'Node.js', 'Typescript']

  return (
    <>
      <Title>Tags - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Tags</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Visualización de metadatos. El componente Tags agrupa etiquetas informativas de manera compacta, ideal para describir atributos, categorizar contenido o mostrar filtros activos.
          </p>
        </div>

        <div class="space-y-12">
          {/* Galería de Estilos */}
          <Card>
            <Card.Header
              title="Presentación de Atributos"
              subtitle="Diferentes configuraciones visuales para representar metadatos contextuales."
              icon={<Icon icon="material-symbols-light:sell-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-12 p-10 bg-surface-muted rounded-3xl border border-border">
                <div class="space-y-8">
                  <div class="space-y-3">
                    <Badge variant="soft" color="info" size="sm">
                      Colores de Marca
                    </Badge>
                    <div class="space-y-4">
                      <Tags tags={sampleTags} color="primary" />
                      <Tags tags={sampleTags} color="secondary" />
                      <Tags tags={sampleTags} color="success" />
                    </div>
                  </div>
                  <div class="space-y-3">
                    <Badge variant="soft" color="warning" size="sm">
                      Variante Outlined
                    </Badge>
                    <Tags tags={sampleTags} outlined color="warning" />
                  </div>
                </div>
                <div class="space-y-8">
                  <div class="space-y-3">
                    <Badge variant="soft" color="secondary" size="sm">
                      Escalabilidad de Tamaño
                    </Badge>
                    <div class="space-y-6">
                      <Tags tags={['Small', 'Metrics']} size="sm" color="secondary" />
                      <Tags tags={['Medium', 'Standard']} size="md" color="secondary" />
                      <Tags tags={['Large', 'Prominent']} size="lg" color="secondary" />
                    </div>
                  </div>
                  <div class="space-y-3">
                    <Badge variant="soft" color="danger" size="sm">
                      Interacción
                    </Badge>
                    <div class="p-4 bg-surface rounded-2xl border border-border shadow-sm">
                      <p class="text-[10px] text-foreground-subtle mb-3 italic">Haz clic en una etiqueta para ver la respuesta táctil.</p>
                      <Tags tags={['Click Me', 'Interactive']} onClick={t => alert(`Tag: ${t}`)} color="danger" outlined />
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente Tags."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">tags</td>
                      <td class="px-6 py-4 italic text-xs">string[]</td>
                      <td class="px-6 py-4 font-mono">[]</td>
                      <td class="px-6 py-4 italic">Lista de cadenas a visualizar.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">color</td>
                      <td class="px-6 py-4 italic text-xs">MainColorsType</td>
                      <td class="px-6 py-4 font-mono">'default'</td>
                      <td class="px-6 py-4">Esquema cromático de las etiquetas.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">outlined</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic text-balance">Cambia el fondo sólido por un borde definido.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4 italic text-xs">'sm' | 'md' | 'lg'</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4">Escala física de visualización.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default TagsPage
